import {type FormEvent, useState, useEffect} from "react";
import {type IComboBox, type IComboBoxOption} from "@fluentui/react";
import {useDispatch} from "react-redux";


import IsaacTagDialogType from "../types/IsaacTagDialogType";
import useTagHook from "../../../../../hooks/useTagNewHook";
import {addTags, removeTags} from "../../../constructionsNewSlice";

import type ConstructionListItem from "../../../types/ConstructionListItem";

const useAddRemoveTagDialogHook = (
  tags: IComboBoxOption[],
  constructions: ConstructionListItem[],
  type: string | undefined,
  isHidden: boolean,
  handleDialogDismiss: React.Dispatch<React.SetStateAction<boolean>>,
): {
  data: IComboBoxOption[];
  error: string | undefined;
  isLoading: boolean;
  selectedTags: string[] | undefined;
  handleChange: (
    event: FormEvent<IComboBox>,
    option?: IComboBoxOption | undefined,
    index?: number | undefined,
    value?: string | undefined,
  ) => void;
  handleCancel: () => void;
  handleAddDelete: () => Promise<void>;
} => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>();
  const [currentTags, setCurrentTags] = useState<IComboBoxOption[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const {deleteTagsFromConstructions, addTagsToConstructions} = useTagHook();

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!isHidden) {
      setError(undefined);
      if (type === IsaacTagDialogType.ADD && constructions !== undefined) {
        const selectedConstructions = constructions.filter(c => c.isSelected);

        setCurrentTags(
          tags.filter(
            t =>
              !selectedConstructions.every(c =>
                c.tags.some(
                  (tagKey: string) =>
                    tagKey.toUpperCase() === t.key.toString().toUpperCase(),
                ),
              ),
          ),
        );
      } else {
        if (constructions !== undefined) {
          const selectedConstructionsWithTags = constructions.filter(c => c.isSelected && c.tags.length > 0)
          const tagsFromSelectedConstructions: string[] =
            selectedConstructionsWithTags.map(c => c.tags).flat();

          setCurrentTags(
            [...new Set(tagsFromSelectedConstructions)].map(t => ({key: t, text: t})),
          );
        }
      }
      setIsLoading(false);
    }
  }, [constructions, isHidden, tags, type]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleChange = (
    event: FormEvent<IComboBox>,
    option?: IComboBoxOption | undefined,
    index?: number | undefined,
    value?: string | undefined,
  ): void => {
    if (option !== undefined) {
      setSelectedTags(prevState => {
        return option.selected === true
          ? [...prevState, option.text]
          : [...prevState?.filter(o => o !== option.text)];
      });

      return;
    }
    if (value !== undefined) {
      setCurrentTags((preState: IComboBoxOption[]) => [
        ...preState,
        {key: value, text: value, selected: true},
      ]);
      setSelectedTags(prevState => [...prevState, value]);
    }
  };

  const handleCancel = (): void => {
    setSelectedTags([]);
  };

  // Note: refactor this to simplify logic and the way the error is set. Add/Remove logic is very similar
  const handleAddDelete = async (): Promise<void> => {
    setIsLoading(true);
    const selectedTagOptions: IComboBoxOption[] = selectedTags.map(t => ({
      key: t,
      text: t,
    }));
    const constructionIds = constructions
      .filter(c => c.isSelected)
      .map(c => c.id);

    if (type === IsaacTagDialogType.ADD) {
      const result = await addTagsToConstructions(
        constructionIds,
        selectedTags ?? [],
      );
      if (!result.some(r => !r.success)) {
        dispatch(
           addTags({
             tags: selectedTagOptions,
             constructionIds,
           }),
        );
        setSelectedTags([]);
        handleDialogDismiss(true);
      } else {
        setError(
          result.some(r => !r.success) ? "Failed to add tag/s." : undefined,
        );
      }
    } else {
      const result = await deleteTagsFromConstructions(
        constructionIds,
        selectedTags ?? [],
      );
      if (!result.some(r => !r.success)) {
        dispatch(
           removeTags({
             tags: selectedTagOptions,
             constructionIds,
           }),
        );
        setSelectedTags([]);
        handleDialogDismiss(true);
      } else {
        setError(
          result.some(r => !r.success) ? "Failed to remove tag/s." : undefined,
        );
      }
    }

    setIsLoading(false);
  };

  return {
    data: currentTags,
    isLoading,
    error,
    selectedTags,
    handleChange,
    handleCancel,
    handleAddDelete,
  };
};

export default useAddRemoveTagDialogHook;
